import { cache } from 'react';
import { logger } from '@tuic/logger';
import { Account } from '../../index';
import { client } from '../../generated/account/client.gen';

export type ApiKitProfile = Account.Profile;

type GetProfile = (idToken: string) => Promise<ApiKitProfile | null>;

/**
 * Fetches the user's profile.
 */
export const getProfile: GetProfile = async idToken => {
  let { data, error } = await Account.getProfile({
    auth: idToken,
  });

  if (error) {
    logger.error(`Failed fetching user's profile. ID Token: ${idToken}`);
    logger.error(error);

    return null;
  }

  return data ?? null;
};

/**
 * Fetches the user's profile only once during a request.
 */
export const memoizedGetProfile: GetProfile = cache(getProfile);

export const updateProfile = async (
  args: Account.ProfileRequest & { idToken: string },
): Promise<boolean> => {
  let { idToken, ...body } = args;

  let { error } = await Account.updateProfile({
    body,
    auth: idToken,
  });

  if (error) {
    logger.error(`[Account] Failed updating profile. ${JSON.stringify(args)}`);
    logger.error(error);

    return false;
  }

  return true;
};

export enum ImpersonateProfileStatus {
  SUCCESS,
  GENERAL_ERROR,
}

type ImpersonateProfileHandler = (args: {
  impersonationToken: string;
}) => Promise<
  | { status: ImpersonateProfileStatus.SUCCESS; token: string }
  | { status: ImpersonateProfileStatus.GENERAL_ERROR; token: null }
>;
export const impersonateProfile: ImpersonateProfileHandler = async args => {
  let { impersonationToken } = args;

  let { data, error } = await client.post<any, unknown, false>({
    url: '/profile/impersonate',
    body: {
      token: impersonationToken,
    },
  });

  if (error) {
    return {
      status: ImpersonateProfileStatus.GENERAL_ERROR,
      token: null,
    };
  }

  if (
    typeof data === 'object' &&
    'token' in data &&
    typeof data.token === 'string'
  ) {
    return {
      status: ImpersonateProfileStatus.SUCCESS,
      token: data.token,
    };
  }

  return {
    status: ImpersonateProfileStatus.GENERAL_ERROR,
    token: null,
  };
};
